import {
    PositionModel,
    Side,
    ContractDate,
    CommodityModel,
    LocationModel
} from '../Generated/Commodity-Management-Api';

export interface PositionAggregation {
    commodity: CommodityModel;
    budgetDate: ContractDate;
    location: LocationModel;
    buyVolume: number;
    buyWeightedPrice: number;
    sellVolume: number;
    sellWeightedPrice: number;
    volumeDifference: number;
    gainLoss: number;
    positions: PositionModel[];
}

export const aggregatePositions = (positions: PositionModel[]): PositionAggregation[] => {
    const groupedPositions = groupPositionsByKey(positions);

    return Array.from(groupedPositions.values()).map(group =>
        calculateAggregation(group)
    );
};

const getGroupKey = (position: PositionModel): string => {
    const commodityId = position.commodity.commodityId;
    const budgetYear = position.budgetDate?.year ?? 0;
    const budgetMonth = position.budgetDate?.month ?? 0;
    const locationId = position.location.locationId;

    return `${commodityId}|${budgetYear}-${budgetMonth}|${locationId}`;
};

const groupPositionsByKey = (positions: PositionModel[]): Map<string, PositionModel[]> => {
    const grouped = new Map<string, PositionModel[]>();

    for (const position of positions) {
        const key = getGroupKey(position);
        const existing = grouped.get(key) || [];
        existing.push(position);
        grouped.set(key, existing);
    }

    return grouped;
};

const calculateAggregation = (positions: PositionModel[]): PositionAggregation => {
    const buyPositions = positions.filter(p => p.side === Side.Buy);
    const sellPositions = positions.filter(p => p.side === Side.Sell);

    const buyVolume = sumVolume(buyPositions);
    const buyWeightedPrice = calculateWeightedPrice(buyPositions);

    const sellVolume = sumVolume(sellPositions);
    const sellWeightedPrice = calculateWeightedPrice(sellPositions);

    const volumeDifference = buyVolume - sellVolume;
    const gainLoss = calculateGainLoss(sellPositions, buyWeightedPrice);

    const firstPosition = positions[0];

    return {
        commodity: firstPosition.commodity,
        budgetDate: firstPosition.budgetDate,
        location: firstPosition.location,
        buyVolume,
        buyWeightedPrice,
        sellVolume,
        sellWeightedPrice,
        volumeDifference,
        gainLoss,
        positions
    };
};

export const sumVolume = (positions: PositionModel[]): number => {
    return positions.reduce((sum, p) => sum + (p.volume || 0), 0);
};

export const calculateWeightedPrice = (positions: PositionModel[]): number => {
    const totalVolume = sumVolume(positions);

    if (totalVolume === 0) {
        return 0;
    }

    const weightedSum = positions.reduce(
        (sum, p) => sum + ((p.price || 0) * (p.volume || 0)),
        0
    );

    return weightedSum / totalVolume;
};

export const calculateGainLoss = (
    sellPositions: PositionModel[],
    buyWeightedPrice: number
): number => {
    // Gain/Loss = (Sell Price - Buy Price) * Sell Volume for each sell position
    return sellPositions.reduce((total, position) => {
        const priceDiff = (position.price || 0) - buyWeightedPrice;
        return total + (priceDiff * (position.volume || 0));
    }, 0);
};